/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import styled from '@emotion/styled'
import BsButton from 'react-bootstrap/Button'
import { startCase } from 'lodash'

import { TableWrapper } from 'components/atoms/TableWrapper'
import DataTable from 'components/atoms/DataTable'
import Link from 'components/atoms/Link'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import { useLabPermission } from 'hooks/useLabPermission'
import { useGetDevicesWithAccessoriesDueQuery } from 'store/services/devices'
import type { AccessoriesDueListItem, AccessoriesDueListQuery, DueAccessory } from 'store/types'
import routes from '../constants/routes'
import { UserPermission } from '../user/permissions'
import { AccessoryModal } from './AccessoryModal'
import { deviceManagementPath } from './shelfLifeNotices'

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
  }
`

// accessories that come due
const ACCESSORY_FILTERS: { value: DueAccessory['type'] | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'PROBE' as DueAccessory['type'], label: 'Probe' },
  { value: 'PATCH' as DueAccessory['type'], label: 'Patch' },
  { value: 'POSITION_SENSOR' as DueAccessory['type'], label: 'Position sensor' },
]

type Row = { row: { original: AccessoriesDueListItem } }

type Replacement = { device: AccessoriesDueListItem; due: DueAccessory }

type SortOptions = { id?: string; desc?: boolean } | undefined

// TES-3191: the Accessory Replacement bucket. A device is listed once an accessory reaches its
// number of uses; replacing the accessory takes it off the list.
const AccessoriesDue = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [accessory, setAccessory] = useState<DueAccessory['type'] | ''>('')
  const [sortOptions, setSortOptions] = useState<SortOptions>()
  const [replacement, setReplacement] = useState<Replacement | null>(null)
  const canManageDevices = useLabPermission(UserPermission.MANAGE_DEVICES)

  const { data, isLoading } = useGetDevicesWithAccessoriesDueQuery(
    {
      page: currentPage,
      size: pageSize,
      ...(search && { search }),
      ...(accessory && { accessory }),
      // the column accessors are the sort fields the list takes
      ...(sortOptions?.id && {
        sortField: sortOptions.id as AccessoriesDueListQuery['sortField'],
        sortOrder: sortOptions.desc ? 'desc' : 'asc',
      }),
    },
    { refetchOnFocus: true },
  )

  const header = [
    {
      Header: 'Device',
      accessor: 'serial',
      className: 'col-2',
      Cell: ({ row }: Row) => (
        <Link to={deviceManagementPath(row.original.id)}>
          {row.original.serial || `#${row.original.id}`}
        </Link>
      ),
    },
    {
      Header: 'Label',
      accessor: 'label',
      className: 'col-1',
      Cell: ({ row }: Row) => row.original.label || '—',
    },
    { Header: 'Type', accessor: 'brandName', className: 'col-2' },
    {
      Header: 'Status',
      accessor: 'state',
      className: 'col-1',
      Cell: ({ row }: Row) => startCase(row.original.state.toLowerCase()),
    },
    {
      Header: 'Due for Replacement',
      accessor: 'accessoriesDue',
      className: 'col-5',
      disableSortBy: true,
      Cell: ({ row }: Row) => (
        <div>
          {row.original.accessoriesDue.map((due: DueAccessory) => (
            <div
              key={`${due.type}-${due.slot}`}
              className="d-flex align-items-center justify-content-between py-1"
              data-testid={`due-${row.original.id}-${due.type}-${due.slot}`}>
              <span>
                <strong>{due.label}</strong>{' '}
                <span className="text-muted">{due.serial || 'no serial'}</span>
                <span className="badge badge-warning ml-2">
                  {due.uses} / {due.limit} uses
                </span>
              </span>
              {canManageDevices && (
                <BsButton
                  size="sm"
                  variant="outline-primary"
                  className="ml-3"
                  onClick={() => setReplacement({ device: row.original, due })}>
                  Replace
                </BsButton>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      Header: 'Out on Order',
      accessor: 'currentOrderId',
      className: 'col-1',
      Cell: ({ row }: Row) =>
        row.original.currentOrderId ? (
          <Link to={`${routes.index}${routes.order.edit}${row.original.currentOrderId}`}>
            #{row.original.currentOrderId}
          </Link>
        ) : (
          '—'
        ),
    },
  ]

  return (
    <DashboardContent
      title="Accessory Replacement"
      icon="fas fa-tools"
      content={
        <div className="card card-primary">
          {/* not a card-header: AdminLTE paints those blue inside card-primary */}
          <div className="d-flex flex-wrap align-items-center px-3 pt-3">
            <div className="btn-group btn-group-sm mr-3" role="group" aria-label="Accessory">
              {ACCESSORY_FILTERS.map((filter) => (
                <BsButton
                  key={filter.value || 'all'}
                  variant={accessory === filter.value ? 'primary' : 'outline-primary'}
                  aria-pressed={accessory === filter.value}
                  onClick={() => {
                    setAccessory(filter.value)
                    setCurrentPage(0)
                  }}>
                  {filter.label}
                </BsButton>
              ))}
            </div>
            <span className="text-muted">
              Devices with an accessory that has reached its number of uses. Replacing it takes the
              device off this list.
            </span>
          </div>
          <StyledTableWrapper className="card-body row">
            <DataTable
              tableKey="accessory_replacement"
              data={data}
              status={isLoading}
              tableColumns={header}
              entityName="devices"
              tableDataEmptyText="No device has an accessory due for replacement."
              pageSize={pageSize}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              setSortOptions={setSortOptions}
              searchFilter={search}
              setSearchFilter={(value: string) => {
                setSearch(value.trim())
                setCurrentPage(0)
              }}
              searchPlaceholder="Serial, label or type"
            />
          </StyledTableWrapper>
          <AccessoryModal
            deviceId={replacement?.device.id ?? 0}
            deviceName={replacement?.device.serial || replacement?.device.label || ''}
            slot={replacement?.due ?? null}
            current={replacement?.due}
            onClose={() => setReplacement(null)}
          />
        </div>
      }
      breadcrumb={[{ label: 'Accessory Replacement' }]}
    />
  )
}

export default AccessoriesDue
